import { Link, StyleSheet, Text } from '@react-pdf/renderer';
import React from 'react';

import { CarrierFactory } from '../../../../../../common/carrierConfig/carrierFactory';
import { Carriers } from '../../../../../../common/constants/carriers';
import { LetterFormData } from '../hooks/useLetterForm';
import { Fields, Sources } from '../types';

export interface WithdrawalLetterContentProps {
  source: Sources;
  formData: LetterFormData;
  carrier: Carriers;
  isJuvenile?: boolean;
}

const WithdrawalLetterContent = ({
  source,
  formData,
  carrier,
  isJuvenile = false,
}: WithdrawalLetterContentProps) => (
  <>
    {isJuvenile ? (
      <Text style={styles.text}>
        Thank you for applying for life insurance with{' '}
        {CarrierFactory.getCarrierConfig(carrier)?.getCarrierDisplayName()}.
        This letter is for your information only.
      </Text>
    ) : (
      <Text style={styles.text}>
        Thank you for your application for life insurance with{' '}
        {CarrierFactory.getCarrierConfig(carrier)?.getCarrierDisplayName()}.
      </Text>
    )}
    <WithdrawalDecision
      source={source}
      formData={formData}
      carrier={carrier}
      isJuvenile={isJuvenile}
    />
  </>
);

const ReapplyLine = ({ isJuvenile }: { isJuvenile: boolean }) =>
  isJuvenile ? (
    <Text style={styles.text}>
      Should you like to be reconsidered for coverage on the proposed life
      insured, a new application will be required. Thus, you will need to
      reapply.
    </Text>
  ) : (
    <Text style={styles.text}>
      Should you like to be reconsidered for coverage, a new application will be
      required. Thus, you will need to reapply.
    </Text>
  );

const WithdrawalDecision = ({
  source,
  formData,
  isJuvenile = false,
}: WithdrawalLetterContentProps) => {
  switch (source) {
    case Sources.eti:
      return (
        <>
          {isJuvenile ? (
            <Text style={styles.text}>
              To date, we have not received all of the requirements necessary to
              review your application. Specifically, we emailed you a short
              questionnaire to complete regarding the proposed life insured,
              which required your response in order for us to proceed with
              further consideration of your application. We have not received
              your response. Therefore, we are unable to continue review of your
              life insurance application and the application has been
              withdrawn.
            </Text>
          ) : (
            <Text style={styles.text}>
              To date, we have not received all of the requirements necessary to
              review your application.{'\n'}
              Specifically, we emailed you a short questionnaire to complete,
              which required your response in order for us to proceed with
              further consideration of your application. We have not received
              your response. Therefore, we are unable to continue review of your
              life insurance application, and your application has been
              withdrawn.
            </Text>
          )}
          <ReapplyLine isJuvenile={isJuvenile} />
        </>
      );
    case Sources.lackOfAps:
      return (
        <>
          {isJuvenile ? (
            <Text style={styles.text}>
              To date, we have not received all of the requirements necessary to
              review your application. In order to proceed with further
              consideration of your application, we requested a copy of the
              proposed life insured’s medical records from{' '}
              {formData[Fields.medicalFacilityOrProvider]}. We have not been
              able to secure records from the doctor. Therefore, we are unable
              to continue review of your life insurance application and your
              application has been withdrawn.
            </Text>
          ) : (
            <Text style={styles.text}>
              To date, we have not received all of the requirements necessary to
              review your application. In order to proceed with further
              consideration of your application, we requested a copy of your
              medical records from {formData[Fields.medicalFacilityOrProvider]}.
              We have not been able to secure records from your doctor.
              Therefore, we are unable to continue review of your life insurance
              application, and your application has been withdrawn.
            </Text>
          )}
          <ReapplyLine isJuvenile={isJuvenile} />
        </>
      );
    case Sources.lackOfInformation:
      return (
        <>
          <Text style={styles.text}>
            To date, we have not received all of the requirements necessary to
            review your application. Specifically, we requested additional
            information from you regarding {formData[Fields.information]}. We
            have not received adequate detail enough to continue underwriting.
            Therefore, we are unable to continue review of your life insurance
            application, and your application has been withdrawn.
          </Text>
          <ReapplyLine isJuvenile={isJuvenile} />
        </>
      );
    case Sources.miscellaneous:
      return (
        <>
          {isJuvenile ? (
            <Text style={styles.text}>
              To date, we have not received all of the requirements necessary to
              review your application. Specifically,{' '}
              {formData[Fields.information]}. Therefore, we are unable to
              continue review of your life insurance application on the proposed
              life insured, and your application has been withdrawn.
            </Text>
          ) : (
            <Text style={styles.text}>
              To date, we have not received all of the requirements necessary to
              review your application. Specifically,{' '}
              {formData[Fields.information]}. Therefore, we are unable to
              continue review of your life insurance application, and your
              application has been withdrawn.
            </Text>
          )}
          <ReapplyLine isJuvenile={isJuvenile} />
        </>
      );
    case Sources.pii:
      return (
        <>
          {isJuvenile ? (
            <Text style={styles.text}>
              We regret to inform you that we have formally withdrawn your
              application for life insurance coverage. The reason for the
              withdrawal is due to an error in your application. Specifically,
              the application does not accurately capture the proposed life
              insured’s correct {formData[Fields.piiDataType]}. Due to this
              discrepancy, we are unable to continue review of your life
              insurance application and your application has been withdrawn.
            </Text>
          ) : (
            <Text style={styles.text}>
              We regret to inform you that we have formally withdrawn your
              application for life insurance coverage. The reason for the
              withdrawal is due to an error in your application. Specifically,
              the application does not accurately capture your correct{' '}
              {formData[Fields.piiDataType]}. Due to this discrepancy, we are
              unable to continue review of your life insurance application, and
              your application has been withdrawn.
            </Text>
          )}
          <ReapplyLine isJuvenile={isJuvenile} />
        </>
      );
    case Sources.specialCharacter:
      return (
        <>
          <Text style={styles.text}>
            We regret to inform you that we have formally withdrawn your
            application for life insurance coverage. The reason for the
            withdrawal is due to an unusual special character used in your name,
            on the application. If the character is not part of your actual full
            legal name, we cannot accept an application reflecting a special
            character in the name.
          </Text>
          <ReapplyLine isJuvenile={isJuvenile} />
        </>
      );
    case Sources.unresolvedMib:
      return (
        <>
          <Text style={styles.text}>
            To date, we have not received all of the requirements necessary to
            review your application. Specifically, we received information from
            a third party source, and we have not been able to reconcile this
            information based on the details you provided in our questionnaire.
          </Text>
          <Text style={styles.text}>
            The information obtained was in a consumer file received from the
            Medical Information Bureau (MIB). During the application process, we
            gave you a written description of the MIB, which is a consumer
            reporting agency that operates an information exchange on behalf of
            insurers. This authorization informed you that the MIB will arrange
            disclosure of any information it may have in your file. When we
            evaluated your application, the MIB sent a brief, coded report about
            you, as you authorized in your application.
          </Text>
          <Text style={styles.text}>
            To obtain a copy of your MIB Consumer File at no charge to you,
            please contact MIB <Text style={styles.bold}>within 60 days</Text>{' '}
            of the date of this letter. You may contact MIB Information Office
            by mail, phone or online requests at:
          </Text>
          <Text style={styles.text}>
            MIB{'\n'}
            50 Braintree Hill Park, Suite 400{'\n'}
            Braintree, MA 02184-8734{'\n'}
            <Link href="tel:18666926901">1-866-692-6901</Link>
            {'\n'}
            <Link href="https://www.mib.com">www.mib.com</Link>
          </Text>
          <Text style={styles.text}>
            Please be aware that the Medical Information Bureau (MIB) only
            provides consumer related data and/or information. This agency is
            not involved in, nor did they make any, underwriting decisions
            regarding your application.
          </Text>
          <Text style={styles.text}>
            The attached notice describes your rights to access the information
            obtained for our files and explains the steps you may take if you
            believe the information is incorrect. This information supplements
            the material already given to you at the time of your application
            for coverage.
          </Text>
        </>
      );
    case Sources.custom:
      return (
        <>
          <Text style={styles.text}>{formData[Fields.customContent]}</Text>
        </>
      );
    default:
      return null;
  }
};

const styles = StyleSheet.create({
  text: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 12,
  },
  bold: {
    fontWeight: '700',
  },
});

export default WithdrawalLetterContent;
